import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { Spinner } from '@frontend/components/common';
import { DefaultModal, ModalType } from '@frontend/components/mobile';
import { EventLevel, EventType } from '@frontend/events/types';
import { CommonMessage, useLocale } from '@frontend/lang';
import { HistoryEventDetails, TransactionHistory, getHistoryEventTitleClassName, mapEventDataToDisplay } from '@frontend/transaction/common';
import { FormattedMessage } from 'react-intl';

import { useMobileDragModal } from '../../modal/mobile-drag-modal/mobile-drag-modal.controller';
import useTransactionHistoryModal from './transaction-history-modal.controller';

const ID = 'nivaliz-transaction-history-modal';

export interface TransactionHistoryModalProps {
    show: boolean;
    handleClose: () => void;
    transactionId: string;
}

const TransactionHistoryModal = (props: TransactionHistoryModalProps) => {
    const { locale } = useLocale();
    const viewProps = useTransactionHistoryModal(props);
    useMobileDragModal(props);

    return (
        <DefaultModal
            id={ID}
            show={props.show}
            handleClose={props.handleClose}
            type={ModalType.MOBILE_BOTTOM}
            className='bg-background-default flex w-full flex-col overflow-hidden rounded-t-3xl'>
            <div className='flex w-full flex-col px-5 pt-3 pb-8'>
                <div
                    id='mobile-drag'
                    className='mb-2 flex cursor-grab touch-none justify-center pb-2 active:cursor-grabbing'>
                    <hr className='bg-border-default h-1 w-24 rounded-full border-0' />
                </div>
                <div className='mb-4 flex items-center justify-between'>
                    <h2 className='text-foreground-default text-lg font-semibold'>{CommonMessage.OBJECTS.TRANSACTION.HISTORY}</h2>
                    <button
                        type='button'
                        className='text-brand-muted text-sm font-medium'
                        onClick={props.handleClose}>
                        {CommonMessage.BUTTONS.CLOSE}
                    </button>
                </div>
                <div className='modal-body no-scrollbar max-h-[70dvh] min-h-[40dvh] flex-1 overflow-y-auto'>
                    {viewProps.isLoading && (
                        <div className='flex w-full justify-center py-8'>
                            <Spinner />
                        </div>
                    )}
                    {!viewProps.isLoading && viewProps.isEmpty && (
                        <p className='text-brand-muted py-6 text-sm font-medium'>
                            <FormattedMessage
                                id='mobile-nivaliz.my.my-nivaliz-transaction-detail.history.empty'
                                description='Empty state when a Nivaliz transaction has no history events'
                                defaultMessage='No history yet'
                            />
                        </p>
                    )}
                    {!viewProps.isLoading && !viewProps.isEmpty && (
                        <TransactionHistory
                            transactionId={props.transactionId}
                            showChildren
                            queryParams={{
                                level: [EventLevel.INFO, EventLevel.ERROR],
                                type: [EventType.TRANSACTION_HISTORY_LOGGED, EventType.NOTIFICATION_SENT]
                            }}
                            content={(event) => (
                                <div className='flex flex-col gap-1'>
                                    <p className={getHistoryEventTitleClassName(event)}>{mapEventDataToDisplay(event.data)}</p>
                                    <HistoryEventDetails
                                        event={event}
                                        timestamp={formatDateTimeWithLocale(new Date(event.creation_timestamp), locale ?? 'en', DateFormat.DD_MMMM_HH_MM)}
                                    />
                                </div>
                            )}
                        />
                    )}
                </div>
            </div>
        </DefaultModal>
    );
};

export default TransactionHistoryModal;
